// Local storage utilities
// Local storage utilities

export class StorageService {
  private prefix: string

  constructor(prefix: string = 'ai-chat') {
    this.prefix = prefix
  }

  private getKey(key: string): string {
    return `${this.prefix}:${key}`
  }

  setItem(key: string, value: any): void {
    try {
      const serialized = JSON.stringify(value)
      localStorage.setItem(this.getKey(key), serialized)
    } catch (error) {
      console.error('Failed to save to localStorage:', error)
    }
  }

  getItem<T>(key: string, defaultValue?: T): T | null {
    try {
      const item = localStorage.getItem(this.getKey(key))
      if (item === null) {
        return defaultValue || null
      }
      return JSON.parse(item)
    } catch (error) {
      console.error('Failed to read from localStorage:', error)
      return defaultValue || null
    }
  }

  removeItem(key: string): void {
    try {
      localStorage.removeItem(this.getKey(key))
    } catch (error) {
      console.error('Failed to remove from localStorage:', error)
    }
  }

  clear(): void {
    try {
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith(this.prefix + ':')) {
          localStorage.removeItem(key)
        }
      })
    } catch (error) {
      console.error('Failed to clear localStorage:', error)
    }
  }
}

// Export singleton instance
export const storage = new StorageService()

// Specific storage utilities
export const storageKeys = {
  THEME: 'theme',
  SIDEBAR_OPEN: 'sidebar-open',
  SYSTEM_PROMPT: 'system-prompt',
  RECENT_CONVERSATIONS: 'recent-conversations',
  USER_PREFERENCES: 'user-preferences',
  TEMPLATE_APPLIED: 'template-applied',
  DRAFT_MESSAGE: 'draft-message',
  INPUT_HEIGHT: 'input-height',
  SHAREPOINT_SEARCH_ENABLED: 'sharepoint-search-enabled',
} as const

export function getTheme(): 'light' | 'dark' {
  return storage.getItem(storageKeys.THEME, 'light') || 'light'
}

export function setTheme(theme: 'light' | 'dark'): void {
  storage.setItem(storageKeys.THEME, theme)
}

export function getSidebarOpen(): boolean {
  return storage.getItem(storageKeys.SIDEBAR_OPEN, true) ?? true
}

export function setSidebarOpen(open: boolean): void {
  storage.setItem(storageKeys.SIDEBAR_OPEN, open)
}

export function getSystemPrompt(): string {
  return storage.getItem(storageKeys.SYSTEM_PROMPT, '') || ''
}

export function setSystemPrompt(prompt: string): void {
  storage.setItem(storageKeys.SYSTEM_PROMPT, prompt)
}

export function getRecentConversations(): number[] {
  return storage.getItem(storageKeys.RECENT_CONVERSATIONS, []) || []
}

export function setRecentConversations(conversationIds: number[]): void {
  storage.setItem(storageKeys.RECENT_CONVERSATIONS, conversationIds)
}

export function addRecentConversation(conversationId: number): void {
  const recent = getRecentConversations()
  const updated = [conversationId, ...recent.filter(id => id !== conversationId)].slice(0, 10)
  setRecentConversations(updated)
}

export function getUserPreferences(): Record<string, any> {
  return storage.getItem(storageKeys.USER_PREFERENCES, {}) || {}
}

export function setUserPreferences(preferences: Record<string, any>): void {
  storage.setItem(storageKeys.USER_PREFERENCES, preferences)
}

export function getSharepointSearchEnabled(): boolean {
  return storage.getItem(storageKeys.SHAREPOINT_SEARCH_ENABLED, false) ?? false
}

export function setSharepointSearchEnabled(enabled: boolean): void {
  storage.setItem(storageKeys.SHAREPOINT_SEARCH_ENABLED, enabled)
}

